import { defineStore } from 'pinia';
import { ref, computed, nextTick } from 'vue';
import {
  ApiError,
  DEFAULT_BID_INCREMENT_PERCENT,
  DEFAULT_STARTING_BID_PERCENT,
  LEGACY_STARTING_BID_PERCENT,
  type AuctionEvent,
  type AuctionEventCreateInput,
} from '@neon-one/auctions-types';
import type { SharedKernel } from '@/shell';
import { useCurrentAuction } from '@/shell';
//

import { configureApi } from './api';
import { usePendingEdits, type StageableField } from '@/composables/usePendingEdits';
import { stagedDiff, filterStagedKeys, displayFor } from './staging';
import { useAdminCatalogStore } from '@/features/admin-catalog/store';

export type Kernel = SharedKernel;

// Pre 2026-05-14 the Configure surface was a 6-step wizard; the
// design-canonical integration collapsed it to a single page. The
// step-state (STEP_ORDER, ConfigureStepId, currentStep, setStep,
// stepIndex) is deleted — every wizard-step consumer was removed.
// DraftState remains because the form bindings are unchanged.

export interface PublishFailure {
  check: string;
  message: string;
}

export const DefaultAmountMode = { Fixed: 'fixed', Percent: 'percent' } as const;
export type DefaultAmountMode = (typeof DefaultAmountMode)[keyof typeof DefaultAmountMode];

const MINIMUM_AMOUNT_CENTS = 1;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface DraftState {
  // Top-level required fields per AuctionEventCreateInput.
  slug: string;
  name: string;
  descriptionHtml: string;
  campaignId: string | null;
  campaignName: string | null;
  crmAutoSyncEnabled: boolean;
  // Basic step
  auctionOpenAt: string;
  auctionCloseAt: string;
  timezone: string;
  closingSoonMinutes: number;
  // Bidding step
  paymentMethodRequirement: 'required' | 'allow_pay_later';
  autoCheckoutMode: 'auto_charge' | 'offer_default';
  donorCoveredFeesEnabled: boolean;
  /** Fixed bid-increment default in cents; null while the field is empty. */
  defaultIncrement: number | null;
  /** % of FMV for the increment default; null = fixed mode via defaultIncrement. */
  defaultIncrementPercent: number | null;
  /** % of FMV for the starting-bid default (used when no fixed amount is set). */
  defaultStartingBidPercent: number;
  /** Fixed starting-bid default in cents; null while empty or in percent mode. */
  defaultStartingBidFixed: number | null;
  /** Draft-only mode preserves a fixed amount being edited to empty. */
  defaultStartingBidMode: DefaultAmountMode;
  proxyBidding: boolean;
  // Registration step
  onlineAccess: 'open' | 'invite_only';
  registrationOpenAt: string | null;
  registrationCloseAt: string | null;
  registrationCapacity: number | null;
  // Notifications step (template-string stubs land in M5)
  notificationTemplates: { kind: string; subject: string; body: string }[];
  termsAndConditionsHtml: string;
  termsAndConditionsEnabled: boolean;
}

// The draft's pre-adoption state, and the source of the bidding-rule values
// CreateAuctionDrawer seeds a new auction with. Not a mirror of the org's
// auction defaults: paymentMethodRequirement / autoCheckoutMode /
// donorCoveredFeesEnabled are resolved server-side from Settings > Auction
// defaults, so the values here for those three are placeholders that hold only
// until `adopt` hydrates the draft from the event.
export const DEFAULT_DRAFT = {
  slug: '',
  name: '',
  descriptionHtml: '',
  campaignId: null,
  campaignName: null,
  crmAutoSyncEnabled: false,
  auctionOpenAt: '',
  auctionCloseAt: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  closingSoonMinutes: 5,
  // Placeholders only — the org's Settings > Auction defaults decide what a new
  // auction actually gets, and `adopt` overwrites all three from the event. Do
  // not read these as the product default for any auction.
  paymentMethodRequirement: 'required',
  autoCheckoutMode: 'offer_default',
  donorCoveredFeesEnabled: true,
  defaultIncrement: 2500,
  // New auctions open in percent-of-value mode for both defaults. 30-40% of
  // value opens bidding without giving one-bid items away, and ~10% steps
  // keep momentum; auctions that predate the feature keep their backfilled
  // 10% starting bid and stay in fixed mode for the increment.
  defaultIncrementPercent: DEFAULT_BID_INCREMENT_PERCENT,
  defaultStartingBidPercent: DEFAULT_STARTING_BID_PERCENT,
  defaultStartingBidFixed: null,
  defaultStartingBidMode: DefaultAmountMode.Percent,
  proxyBidding: true,
  onlineAccess: 'open',
  registrationOpenAt: null,
  registrationCloseAt: null,
  registrationCapacity: null,
  notificationTemplates: [],
  termsAndConditionsHtml: '',
  // Backend defaults ON when the org has a global default T&C;
  // ConfigurePage mirrors that for brand-new drafts once org settings
  // load (GT1-348).
  termsAndConditionsEnabled: false,
} satisfies DraftState;

// BR-18: once an auction has bids these are locked. The page disables the
// inputs, but the autosave PATCH sends the whole draft — so an edit made in
// the moment before a bid lands would still be transmitted unless the key is
// stripped here too. The item-pricing defaults belong in this list for the
// same reason the payment fields do.
const FINANCIAL_LOCKED_KEYS = [
  'paymentMethodRequirement',
  'autoCheckoutMode',
  'donorCoveredFeesEnabled',
  'registrationCapacity',
  'closingSoonMinutes',
  'registrationOpenAt',
  'registrationCloseAt',
  'defaultBidIncrementCents',
  'defaultBidIncrementPercent',
  'defaultStartingBidPercent',
  'defaultStartingBidCents',
] as const;

// Server-side edit locks (BR-18 once bids exist; scheduling on a closed
// auction) answer with `lockedFields`, not `fieldErrors` — the fields are
// valid, they're just no longer movable. Naming them is the whole value of the
// message: the inputs are disabled on this page, so an admin who hits this is
// being blocked by a value they cannot see and cannot retry their way out of.
const LOCKED_FIELD_LABELS: Record<string, string> = {
  paymentMethodRequirement: 'Require payment method',
  autoCheckoutMode: 'Auto-charge winners',
  donorCoveredFeesEnabled: 'Allow donors to cover processing fees',
  registrationCapacity: 'Registration capacity',
  registrationOpenAt: 'Registration opens',
  registrationCloseAt: 'Registration closes',
  auctionOpenAt: 'Auction start',
  auctionCloseAt: 'Auction close time',
};

/** `lockedFields` off an error body, which carries it as passthrough data. */
function lockedFieldsOf(err: ApiError): string[] {
  const raw = (err.body as { lockedFields?: unknown }).lockedFields;
  return Array.isArray(raw) ? raw.filter((f): f is string => typeof f === 'string') : [];
}

function joinLabels(fields: string[]): string {
  const labels = fields.map((f) => LOCKED_FIELD_LABELS[f] ?? f);
  if (labels.length <= 2) return labels.join(' and ');
  const last = labels[labels.length - 1];
  return `${labels.slice(0, -1).join(', ')}, and ${last}`;
}

/**
 * `closed` discriminates the two locks rather than the field list: the
 * registration fields belong to both sets, so they can't tell them apart.
 */
function lockedFieldsMessage(fields: string[], closed: boolean): string {
  const reason = closed ? 'this auction is closed' : 'bids have already been placed';
  const plural = fields.length === 1 ? 'that setting' : 'those settings';
  return `${joinLabels(fields)} can no longer change: ${reason}. Undo ${plural} to save the rest of your edits.`;
}

/** Short labels for the pending-edits card rows, keyed by stageable field. */
const STAGED_FIELD_LABELS: Record<StageableField, string> = {
  name: 'Title',
  descriptionHtml: 'Description',
  auctionOpenAt: 'Opens at',
  auctionCloseAt: 'Close time',
  defaultIncrement: 'Bid increment',
  defaultIncrementPercent: 'Bid increment',
  defaultStartingBidPercent: 'Starting bid',
  defaultStartingBidFixed: 'Starting bid',
  proxyBidding: 'Proxy bidding',
  termsAndConditionsEnabled: 'Require terms',
  termsAndConditionsHtml: 'Terms & conditions',
};

/** The published value for a stageable field, in draft cents shape. */
function eventValueFor(field: StageableField, event: AuctionEvent): unknown {
  if (field === 'defaultIncrement') return event.defaultBidIncrementCents;
  if (field === 'defaultIncrementPercent') return event.defaultBidIncrementPercent ?? null;
  if (field === 'defaultStartingBidPercent')
    return event.defaultStartingBidPercent ?? LEGACY_STARTING_BID_PERCENT;
  if (field === 'defaultStartingBidFixed')
    return event.defaultStartingBidCents ?? null;
  if (field === 'termsAndConditionsHtml') return event.termsAndConditionsHtml ?? '';
  return event[field as keyof AuctionEvent];
}

/** The current draft value for a stageable field. */
function draftValueFor(field: StageableField, draft: DraftState): unknown {
  return draft[field as keyof DraftState];
}

// A money amount the reader has not finished (empty, or below the minimum) is
// left out of the payload: the update input is partial, so every other edit
// still saves and the server keeps the last valid amount.
function draftToPayload(d: DraftState) {
  return {
    slug: d.slug,
    name: d.name,
    descriptionHtml: d.descriptionHtml,
    auctionOpenAt: d.auctionOpenAt,
    auctionCloseAt: d.auctionCloseAt,
    timezone: d.timezone,
    closingSoonMinutes: d.closingSoonMinutes,
    defaultBidIncrementCents: validAmount(d.defaultIncrement) ? d.defaultIncrement : undefined,
    defaultBidIncrementPercent: d.defaultIncrementPercent,
    defaultStartingBidPercent: d.defaultStartingBidPercent,
    defaultStartingBidCents:
      d.defaultStartingBidMode !== DefaultAmountMode.Fixed
        ? null
        : validAmount(d.defaultStartingBidFixed) ? d.defaultStartingBidFixed : undefined,
    proxyBidding: d.proxyBidding,
    onlineAccess: d.onlineAccess,
    paymentMethodRequirement: d.paymentMethodRequirement,
    autoCheckoutMode: d.autoCheckoutMode,
    donorCoveredFeesEnabled: d.donorCoveredFeesEnabled,
    campaignId: d.campaignId,
    campaignName: d.campaignName,
    crmAutoSyncEnabled: d.crmAutoSyncEnabled,
    registrationOpenAt: d.registrationOpenAt,
    registrationCloseAt: d.registrationCloseAt,
    registrationCapacity: d.registrationCapacity,
    termsAndConditionsHtml: d.termsAndConditionsHtml,
    termsAndConditionsEnabled: d.termsAndConditionsEnabled,
  };
}

function validAmount(value: number | null): value is number {
  return value != null && Number.isInteger(value) && value >= MINIMUM_AMOUNT_CENTS;
}

type MoneyField = 'defaultIncrement' | 'defaultStartingBidFixed';

const MONEY_FIELD_NAMES: Record<MoneyField, string> = {
  defaultIncrement: 'a bid increment',
  defaultStartingBidFixed: 'a starting bid',
};

/**
 * The money fields the reader must still fill. A fixed amount is only required
 * in its fixed mode: in percent mode its input is hidden, and a blank left
 * behind from fixed mode must not block anything.
 */
function incompleteMoneyFields(draft: DraftState): MoneyField[] {
  const incomplete: MoneyField[] = [];
  if (draft.defaultIncrementPercent == null && !validAmount(draft.defaultIncrement)) {
    incomplete.push('defaultIncrement');
  }
  if (draft.defaultStartingBidMode === DefaultAmountMode.Fixed && !validAmount(draft.defaultStartingBidFixed)) {
    incomplete.push('defaultStartingBidFixed');
  }
  return incomplete;
}

export const useConfigureStore = defineStore('configure', () => {
  const currentAuction = useCurrentAuction();
  const auctionUuid = ref<string | null>(null);
  const draft = ref<DraftState>({ ...DEFAULT_DRAFT });

  const moneyErrors = computed(() => {
    const incomplete = incompleteMoneyFields(draft.value);
    return {
      defaultIncrement: incomplete.includes('defaultIncrement') ? 'Enter a bid increment.' : undefined,
      defaultStartingBidFixed: incomplete.includes('defaultStartingBidFixed') ? 'Enter a starting bid.' : undefined,
    };
  });

  const hasBids = computed(() => currentAuction.event?.hasBids ?? false);

  try {
    sessionStorage.removeItem('neon-auctions:configure-draft');
  } catch {
    /* noop */
  }
  const saveState = ref<SaveState>('idle');
  const lastSavedAt = ref<string | null>(null);
  const saveError = ref<string | null>(null);
  // True when the caller that produced this error reports it itself. Publish is
  // a button the admin pressed and its card answers in place; without this the
  // page-level autosave watcher toasts the same failure a second time.
  const saveErrorReportedByCaller = ref(false);
  const fieldErrors = ref<Record<string, string[]>>({});
  const publishFailures = ref<PublishFailure[]>([]);
  const publishing = ref(false);
  const _adopting = ref(false);

  const canPublish = computed(() => publishFailures.value.length === 0 && auctionUuid.value !== null);

  // Bidder-visible edits stage (rather than write through) once the auction
  // is live to bidders. Spec §1: published → scheduled, open → live.
  const stagesEdits = computed(
    () => currentAuction.event?.status === 'published' || currentAuction.event?.status === 'open'
  );

  /** Drop financial-locked keys from a payload when the auction has bids. */
  function applyFinancialFilter<T extends Record<string, unknown>>(payload: T): T {
    if (!hasBids.value) return payload;
    return Object.fromEntries(
      Object.entries(payload).filter(
        ([k]) => !FINANCIAL_LOCKED_KEYS.includes(k as (typeof FINANCIAL_LOCKED_KEYS)[number])
      )
    ) as T;
  }

  function update<K extends keyof DraftState>(field: K, value: DraftState[K]): void {
    draft.value[field] = value;
    if (fieldErrors.value[field as string]) {
      const next = { ...fieldErrors.value };
      delete next[field as string];
      fieldErrors.value = next;
    }
  }

  function adoptFromServer(event: AuctionEvent): void {
    _adopting.value = true;
    auctionUuid.value = event.uuid;
    draft.value = {
      slug: event.slug,
      name: event.name,
      descriptionHtml: event.descriptionHtml,
      campaignId: event.campaignId,
      campaignName: event.campaignName,
      crmAutoSyncEnabled: event.crmAutoSyncEnabled,
      auctionOpenAt: event.auctionOpenAt,
      auctionCloseAt: event.auctionCloseAt,
      timezone: event.timezone,
      closingSoonMinutes: event.closingSoonMinutes,
      paymentMethodRequirement: event.paymentMethodRequirement,
      autoCheckoutMode: event.autoCheckoutMode,
      donorCoveredFeesEnabled: event.donorCoveredFeesEnabled,
      defaultIncrement: event.defaultBidIncrementCents,
      defaultIncrementPercent: event.defaultBidIncrementPercent ?? null,
      defaultStartingBidPercent: event.defaultStartingBidPercent ?? LEGACY_STARTING_BID_PERCENT,
      defaultStartingBidFixed: event.defaultStartingBidCents ?? null,
      defaultStartingBidMode: event.defaultStartingBidCents != null ? DefaultAmountMode.Fixed : DefaultAmountMode.Percent,
      proxyBidding: event.proxyBidding,
      onlineAccess: event.onlineAccess,
      registrationOpenAt: event.registrationOpenAt,
      registrationCloseAt: event.registrationCloseAt,
      registrationCapacity: event.registrationCapacity,
      notificationTemplates: [],
      termsAndConditionsHtml: event.termsAndConditionsHtml ?? '',
      termsAndConditionsEnabled: event.termsAndConditionsEnabled,
    };
    void nextTick(() => {
      _adopting.value = false;
    });
  }

  function applyApiError(err: ApiError): void {
    saveState.value = 'error';
    saveErrorReportedByCaller.value = false;
    if (err.code === 'VALIDATION_FAILED' && err.fieldErrors) {
      fieldErrors.value = err.fieldErrors;
      saveError.value = 'Some fields need attention.';
      return;
    }
    const locked = lockedFieldsOf(err);
    if (locked.length > 0) {
      saveError.value = lockedFieldsMessage(
        locked,
        currentAuction.event?.status === 'closed',
      );
      return;
    }
    saveError.value = 'We couldn’t save the changes. Try again.';
  }

  /**
   * Stage bidder-visible draft edits against the published event and return
   * the list of staged fields (so the caller can exclude them from the PATCH).
   * Fields that match the event again have any pending entry removed. Both the
   * staging and the removal are driven from `stagedDiff` so the card and the
   * payload filter never disagree.
   */
  /**
   * Pending-edits bucket for the current auction. Keyed by UUID, matching
   * BuilderShell's `usePendingEdits(route :id)` — in eng the route param is
   * the auction UUID, not the slug.
   */
  function pendingForCurrent() {
    return usePendingEdits(auctionUuid.value ?? draft.value.slug);
  }

  function syncStagedEdits(event: AuctionEvent): StageableField[] {
    const pending = pendingForCurrent();
    const diff = stagedDiff(draft.value, event);
    const diffSet = new Set(diff);
    for (const field of diff) {
      const previousValue = eventValueFor(field, event);
      const stagedValue = draftValueFor(field, draft.value);
      pending.stageEdit({
        field,
        label: STAGED_FIELD_LABELS[field],
        previous: displayFor(field, previousValue, draft.value.timezone),
        staged: displayFor(field, stagedValue, draft.value.timezone),
        previousValue,
        stagedValue,
      });
    }
    // Remove pending entries for stageable fields that now match the event
    // (composable has no discard-by-field; discard by id). Snapshot first —
    // discardEdit splices the live array `edits.value` points at.
    for (const edit of [...pending.edits.value]) {
      if (!diffSet.has(edit.field)) pending.discardEdit(edit.id);
    }
    return diff;
  }

  /** Snapshot the current draft values for the given fields (pre-adopt). */
  function snapshotStaged(staged: StageableField[]): Map<StageableField | 'defaultStartingBidMode', unknown> {
    const snap = new Map<StageableField | 'defaultStartingBidMode', unknown>();
    if (staged.includes('defaultStartingBidFixed')) {
      snap.set('defaultStartingBidMode', draft.value.defaultStartingBidMode);
    }
    for (const field of staged) snap.set(field, draftValueFor(field, draft.value));
    return snap;
  }

  /**
   * Re-apply staged draft values that adoptFromServer clobbered with the
   * server's (pre-edit) values. Driven from a snapshot of the draft taken
   * before adopt — independent of whether a pending card row was created —
   * so a staged edit always survives the adopt.
   */
  function reapplyStaged(snapshot: Map<StageableField | 'defaultStartingBidMode', unknown>): void {
    for (const [field, value] of snapshot) {
      (draft.value[field as keyof DraftState] as unknown) = value;
    }
  }

  /** The page shows the field errors; the status says the save is not complete. */
  function markMoneyIncomplete(): void {
    saveState.value = 'error';
    saveErrorReportedByCaller.value = false;
    saveError.value = 'Some fields need attention.';
  }

  async function save(): Promise<AuctionEvent | null> {
    const incomplete = incompleteMoneyFields(draft.value);
    // A new auction cannot be created without its money defaults.
    if (!auctionUuid.value && incomplete.length > 0) {
      markMoneyIncomplete();
      return null;
    }
    // Kept so the adopt below cannot refill a field the reader is still typing in.
    const blanks = incomplete.map((field) => [field, draft.value[field]] as const);
    saveState.value = 'saving';
    saveError.value = null;
    fieldErrors.value = {};
    try {
      const event0 = currentAuction.event;
      // On a live/scheduled auction, stage bidder-visible edits and keep them
      // out of the autosave PATCH (same mechanism as FINANCIAL_LOCKED_KEYS).
      const staged: StageableField[] =
        stagesEdits.value && event0 ? syncStagedEdits(event0) : [];
      // Snapshot the staged draft values now: the PATCH excludes them, so the
      // server returns pre-edit values and adoptFromServer would clobber them.
      const stagedSnapshot = snapshotStaged(staged);

      const rawPayload = applyFinancialFilter(draftToPayload(draft.value));
      const payload = filterStagedKeys(rawPayload, staged);

      const event = auctionUuid.value
        ? await configureApi.update(auctionUuid.value, payload)
        : await configureApi.create({
          ...payload,
          // Create requires the fixed increment even in percent mode, where it
          // is hidden and unused; a blank left over from fixed mode takes the
          // placeholder rather than failing the create.
          defaultBidIncrementCents: payload.defaultBidIncrementCents ?? DEFAULT_DRAFT.defaultIncrement,
        } as AuctionEventCreateInput);
      adoptFromServer(event);
      // adoptFromServer clobbered the draft with server values; restore the
      // admin's staged edits and unfinished amounts synchronously (while
      // _adopting is true, so the autosave watcher skips). Must not yield
      // before this runs.
      reapplyStaged(stagedSnapshot);
      for (const [field, value] of blanks) draft.value[field] = value;
      currentAuction.setEvent(event);
      lastSavedAt.value = new Date().toISOString();
      if (blanks.length > 0) markMoneyIncomplete();
      else saveState.value = 'saved';
      return event;
    } catch (e) {
      if (e instanceof ApiError) applyApiError(e);
      else saveError.value = 'We couldn’t save the changes.';
      return null;
    }
  }

  async function publish(): Promise<boolean> {
    if (!auctionUuid.value) return false;
    publishing.value = true;
    publishFailures.value = [];
    try {
      const event = await configureApi.publish(auctionUuid.value);
      adoptFromServer(event);
      currentAuction.setEvent(event);
      // Backend publishAuction() cascades every draft item + bundle to
      // published (auctionLifecycle.ts:488-540). The admin-catalog store
      // still holds the pre-publish snapshot, so refetch items + bundles
      // so the Configure page reflects the cascade without a hard refresh
      // (GT1-422). Refetch failure here does NOT roll back the publish —
      // publish itself succeeded; log a soft error and still return true.
      const catalog = useAdminCatalogStore();
      try {
        await Promise.all([
          catalog.loadItems(auctionUuid.value),
          catalog.loadBundles(auctionUuid.value),
        ]);
      } catch {
        saveError.value = 'Published. Refresh to see updated items.';
      }
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'PUBLISH_VALIDATION_FAILED') {
        publishFailures.value = (e.body.failures ?? []) as PublishFailure[];
      } else {
        saveError.value = 'Publish failed. Try again.';
      }
      return false;
    } finally {
      publishing.value = false;
    }
  }

  /**
   * Discard a staged edit: revert the draft field to the published value
   * and drop the pending entry. The revert
   * mutates the draft, so autosave fires; that save's diff finds no change
   * for this field, so it won't re-create the entry.
   */
  function discardStagedEdit(field: StageableField): void {
    const event = currentAuction.event;
    if (!event) return;
    const pending = pendingForCurrent();
    (draft.value[field as keyof DraftState] as unknown) = eventValueFor(field, event);
    if (field === 'defaultStartingBidFixed') {
      draft.value.defaultStartingBidMode =
        event.defaultStartingBidCents != null ? DefaultAmountMode.Fixed : DefaultAmountMode.Percent;
    }
    const entry = pending.edits.value.find((e) => e.field === field);
    if (entry) pending.discardEdit(entry.id);
  }

  /**
   * Publish all staged edits as a single PATCH (financial filter still
   * applies; the staged filter does not). Adopts the result and clears the
   * pending edits. Returns success for toast handling.
   */
  async function publishStagedEdits(): Promise<boolean> {
    if (!auctionUuid.value) return false;
    const incomplete = incompleteMoneyFields(draft.value);
    if (incomplete.length > 0) {
      // The card that called this reports the reason, which lives on Configure.
      const names = incomplete.map((field) => MONEY_FIELD_NAMES[field]).join(' and ');
      saveError.value = `Enter ${names} in Configure, then publish again.`;
      saveErrorReportedByCaller.value = true;
      return false;
    }
    saveState.value = 'saving';
    saveError.value = null;
    fieldErrors.value = {};
    try {
      const payload = applyFinancialFilter(draftToPayload(draft.value));
      const event = await configureApi.update(auctionUuid.value, payload);
      adoptFromServer(event);
      currentAuction.setEvent(event);
      pendingForCurrent().clearAll();
      saveState.value = 'saved';
      lastSavedAt.value = new Date().toISOString();
      return true;
    } catch (e) {
      if (e instanceof ApiError) applyApiError(e);
      else saveError.value = 'We couldn’t save the changes.';
      saveErrorReportedByCaller.value = true;
      return false;
    }
  }

  function reset(): void {
    auctionUuid.value = null;
    draft.value = { ...DEFAULT_DRAFT };
    saveState.value = 'idle';
    lastSavedAt.value = null;
    saveError.value = null;
    fieldErrors.value = {};
    publishFailures.value = [];
  }

  return {
    _adopting,
    auctionUuid,
    draft,
    hasBids,
    saveState,
    lastSavedAt,
    saveError,
    saveErrorReportedByCaller,
    fieldErrors,
    moneyErrors,
    publishFailures,
    publishing,
    canPublish,
    stagesEdits,
    update,
    adoptFromServer,
    save,
    publish,
    discardStagedEdit,
    publishStagedEdits,
    reset,
  };
});
